import { useEffect, useState } from 'react'

import {
  useToast,
  useDisclosure,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react'
import { useIdleTimer } from 'react-idle-timer'
import { useDispatch } from 'react-redux'

import { logout } from './redux/features/authSlice'
import { calculateMS } from './utils'

const IDLE_TIMEOUT = calculateMS(2, 'h')
const IDLE_WARNING_BEFORE_LOGOUT = calculateMS(5, 'm')
const SESSION_EXPIRED_TOAST_ID = 'session-expired'

function formatCountdown(milliseconds) {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

const IdleTimeout = ({ token }) => {
  const toast = useToast()
  const dispatch = useDispatch()
  const {
    isOpen: isIdleWarningOpen,
    onOpen: openIdleWarning,
    onClose: closeIdleWarning,
  } = useDisclosure()

  const [countdownMs, setCountdownMs] = useState(IDLE_WARNING_BEFORE_LOGOUT)

  function onIdle() {
    closeIdleWarning()

    if (token) {
      dispatch(
        logout({
          callback: () => {
            if (!toast.isActive(SESSION_EXPIRED_TOAST_ID)) {
              toast({
                title: 'Logged out',
                status: 'warning',
                description: 'Your session has expired, please log in again.',
                duration: null,
                isClosable: true,
                position: 'top',
                id: SESSION_EXPIRED_TOAST_ID,
              })
            }
          },
        })
      )
    }
  }

  function onPrompt() {
    if (token) {
      openIdleWarning()
    }
  }

  const onAction = e => {
    if (isIdleWarningOpen) {
      closeIdleWarning()
    }

    if (e.type.includes('mouse') && toast.isActive(SESSION_EXPIRED_TOAST_ID)) {
      toast.close(SESSION_EXPIRED_TOAST_ID)
    }

    idleTimer.reset()
  }

  const idleTimer = useIdleTimer({
    promptBeforeIdle: IDLE_WARNING_BEFORE_LOGOUT,
    onPrompt,
    onIdle,
    onAction,
    timeout: IDLE_TIMEOUT,
    debounce: 250,
    disabled: !token,
  })

  useEffect(() => {
    if (!isIdleWarningOpen) {
      return undefined
    }

    const updateCountdown = () => {
      const remaining = idleTimer.getRemainingTime()
      setCountdownMs(Math.min(IDLE_WARNING_BEFORE_LOGOUT, remaining))
    }

    updateCountdown()
    const intervalId = setInterval(updateCountdown, 1000)

    return () => clearInterval(intervalId)
  }, [idleTimer, isIdleWarningOpen])

  function handleStayLoggedIn() {
    idleTimer.reset()
    closeIdleWarning()
  }

  if (!token) {
    return null
  }

  return (
    <Modal isOpen={isIdleWarningOpen} onClose={handleStayLoggedIn} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Session Expiring Soon</ModalHeader>
        <ModalBody>
          You have been inactive. You will be logged out in{' '}
          {formatCountdown(countdownMs)} unless you continue your session.
        </ModalBody>
        <ModalFooter>
          <Button colorScheme='blue' onClick={handleStayLoggedIn}>
            Stay Logged In
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default IdleTimeout
